// SCR-STAFF-878 (AP-INV-02؛ US-INV-044، 070 تا 072): کاردکس کالا (ورود، خروج، مانده مقداری و ریالی به ازای سند) و گزارش‌های انبار —
// مصرف به تفکیک خدمت/ارائه‌دهنده/انبار، انحراف از BOM، اقلام پرمصرف، رسید به تفکیک تأمین‌کننده، ضایعات به تفکیک علت، مرجوعی، بهای فروش،
// ارزش موجودی و گردش. داده تجمیعی است و هیچ اطلاعات بیمار ندارد.
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { unwrap, type InvKardexRowView, type InvReportRowView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, JalaliDate, JalaliDatePickerRHF, ListPage, Money, toBusinessDate, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { fromMilli, REPORT_KINDS, useItemOptions, useWarehouses, WarehouseSelect, warehouseName, type ReportKind } from './stockCommon';

export const SCR_STAFF_878 = 'SCR-STAFF-878';

type TabId = 'kardex' | 'reports';

function monthStart(): string {
  const today = toBusinessDate(new Date()) ?? '';
  return today.length >= 8 ? `${today.slice(0, 8)}01` : today;
}

const milliText = (milli: number | string | null | undefined) => (milli === null || milli === undefined ? '—' : faDigits(String(fromMilli(milli))));

export function InventoryReportsPage() {
  const { t } = useTranslation('inv');
  const [tab, setTab] = useState<TabId>('kardex');
  const [warehouseId, setWarehouseId] = useState('');
  const { control, watch } = useForm<{ from: string | null; to: string | null }>({ defaultValues: { from: monthStart(), to: toBusinessDate(new Date()) } });
  const from = watch('from') ?? undefined;
  const to = watch('to') ?? undefined;
  return (
    <ListPage
      screenId={SCR_STAFF_878}
      titleKey="inv:reports.title"
      subtitleKey="inv:reports.subtitle"
      filters={
        <Stack spacing={1}>
          <Tabs value={tab} onChange={(_event, value: TabId) => setTab(value)} aria-label={t('inv:reports.title')}>
            <Tab value="kardex" label={t('inv:reports.tabs.kardex')} data-testid="reports-tab-kardex" />
            <Tab value="reports" label={t('inv:reports.tabs.reports')} data-testid="reports-tab-reports" />
          </Tabs>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <JalaliDatePickerRHF control={control} name="from" labelKey="inv:reports.from" />
            <JalaliDatePickerRHF control={control} name="to" labelKey="inv:reports.to" />
            <WarehouseSelect value={warehouseId} onChange={setWarehouseId} includeEmpty testId="reports-warehouse" />
          </Stack>
        </Stack>
      }
    >
      {tab === 'kardex' ? <Kardex warehouseId={warehouseId} from={from} to={to} /> : <Reports warehouseId={warehouseId} from={from} to={to} />}
    </ListPage>
  );
}

function Kardex({ warehouseId, from, to }: { warehouseId: string; from?: string; to?: string }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const items = useItemOptions();
  const warehouses = useWarehouses();
  const [itemId, setItemId] = useState('');
  const kardex = useQuery({
    queryKey: ['inventory', 'kardex', session?.tenantId, itemId, warehouseId, from, to],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/kardex', { params: { query: { itemId, warehouseId: warehouseId || undefined, from, to, limit: 100 } } })),
    enabled: Boolean(itemId),
  });
  const columns: DataTableColumn<InvKardexRowView>[] = [
    { id: 'date', headerKey: 'inv:kardex.columns.date', cell: (row) => <JalaliDate value={row.businessDate} /> },
    { id: 'document', headerKey: 'inv:kardex.columns.document', cell: (row) => <bdi dir="ltr">{row.documentNumber}</bdi> },
    { id: 'kind', headerKey: 'inv:kardex.columns.kind', cell: (row) => t(`inv:movement_kind.${row.kind}`, { defaultValue: row.kind }) },
    { id: 'warehouse', headerKey: 'inv:kardex.columns.warehouse', cell: (row) => warehouseName(warehouses.data?.items, row.warehouseId) },
    { id: 'batch', headerKey: 'inv:kardex.columns.batch', cell: (row) => <bdi dir="ltr">{row.batchNumber ?? '—'}</bdi> },
    { id: 'in', headerKey: 'inv:kardex.columns.in', cell: (row) => milliText(row.inMilli) },
    { id: 'out', headerKey: 'inv:kardex.columns.out', cell: (row) => milliText(row.outMilli) },
    { id: 'remaining', headerKey: 'inv:kardex.columns.remaining', cell: (row) => <span data-testid={`kardex-remaining-${row.documentNumber}`}>{milliText(row.remainingMilli)}</span> },
    { id: 'unitCost', headerKey: 'inv:kardex.columns.unit_cost', cell: (row) => <Money amountMinor={row.unitCost.amount} currency={row.unitCost.currency} /> },
    { id: 'value', headerKey: 'inv:kardex.columns.remaining_value', cell: (row) => <Money amountMinor={row.remainingValue.amount} currency={row.remainingValue.currency} /> },
  ];
  return (
    <DataTable
      captionKey="inv:kardex.caption"
      columns={columns}
      rows={itemId ? kardex.data?.items : []}
      getRowId={(row) => row.id}
      isLoading={Boolean(itemId) && kardex.isPending}
      error={kardex.error}
      onRetry={() => void kardex.refetch()}
      emptyTitleKey={itemId ? 'inv:kardex.empty' : 'inv:kardex.pick_item'}
      toolbar={
        <TextField select size="small" label={t('inv:kardex.item')} value={itemId} onChange={(event) => setItemId(event.target.value)} sx={{ minWidth: 260 }} slotProps={{ htmlInput: { 'data-testid': 'kardex-item' } }}>
          {(items.data?.items ?? []).map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      }
    />
  );
}

function Reports({ warehouseId, from, to }: { warehouseId: string; from?: string; to?: string }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const [kind, setKind] = useState<ReportKind>('consumption-by-service');
  const report = useQuery({
    queryKey: ['inventory', 'reports', session?.tenantId, kind, warehouseId, from, to],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/reports/{kind}', { params: { path: { kind }, query: { warehouseId: warehouseId || undefined, from, to } } })),
  });
  const variance = kind === 'bom-variance';
  const columns: DataTableColumn<InvReportRowView>[] = [
    { id: 'key', headerKey: 'inv:reports.columns.key', cell: (row) => <span data-testid={`report-row-${row.key}`}>{t(`inv:waste_reason.${row.key}`, { defaultValue: row.itemName ?? row.key })}</span> },
    { id: 'item', headerKey: 'inv:reports.columns.item', cell: (row) => row.itemName ?? '—' },
    { id: 'quantity', headerKey: 'inv:reports.columns.quantity', cell: (row) => milliText(row.quantityMilli) },
    ...(variance
      ? ([
          { id: 'reference', headerKey: 'inv:reports.columns.reference', cell: (row) => milliText(row.referenceMilli) },
          { id: 'ratio', headerKey: 'inv:reports.columns.ratio', cell: (row) => (row.ratio === null ? '—' : t('inv:reports.percent', { value: faDigits(String(Math.round(Number(row.ratio) / 10))) })) },
        ] satisfies DataTableColumn<InvReportRowView>[])
      : []),
    { id: 'value', headerKey: 'inv:reports.columns.value', cell: (row) => <Money amountMinor={row.value.amount} currency={row.value.currency} /> },
  ];
  return (
    <DataTable
      captionKey="inv:reports.caption"
      columns={columns}
      rows={report.data?.rows}
      getRowId={(row) => `${row.key}-${row.itemId ?? ''}`}
      isLoading={report.isPending}
      error={report.error}
      onRetry={() => void report.refetch()}
      emptyTitleKey="inv:reports.empty"
      toolbar={
        <TextField select size="small" label={t('inv:reports.kind')} value={kind} onChange={(event) => setKind(event.target.value as ReportKind)} sx={{ minWidth: 260 }} slotProps={{ htmlInput: { 'data-testid': 'report-kind' } }}>
          {REPORT_KINDS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`inv:report_kind.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    />
  );
}
